import React, { useContext, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Button, Icon } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { getCustomerWalletActions } from 'pages/Customers/helpers/utils'
import { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BulkCapabilityResponse } from 'types/capabilities/capabilities'
import {
  CustomerWallet,
  CustomerWalletDepositDetails,
  NestedCustomerDetails,
  OrchestratedCustomerWallet,
} from 'types/customers'
import { ActionIds } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'

type WalletActionButtonsProps = {
  customer: NestedCustomerDetails
  customerWallet: CustomerWallet
  bulkCapabilities: BulkCapabilityResponse
  isFetchingCapabilities: boolean
  isFetchingWalletLegacyId: boolean
  setIsSheetOpen: React.Dispatch<React.SetStateAction<boolean>>
  setWalletModalAction: (value: React.SetStateAction<WalletAction>) => void
  setIsPayWalletModalOpen: (value: React.SetStateAction<boolean>) => void
  cleanupUnifiedPayoutsFlow: () => void
}

export const WalletActionButtons: React.FC<WalletActionButtonsProps> = ({
  customer,
  customerWallet,
  bulkCapabilities,
  isFetchingCapabilities,
  isFetchingWalletLegacyId,
  setIsSheetOpen,
  setWalletModalAction,
  setIsPayWalletModalOpen,
  cleanupUnifiedPayoutsFlow,
}) => {
  const { t } = useTranslation()

  const { enabledUnifiedFiatSendFlow, enableCustomerCryptoWalletTransactions } = useFeatureFlags()
  const { accountCapabilities } = useAccountsContext()
  const { viewOnly: isViewOnlyRole } = useProfileContext() || {}
  const [, setWalletAction] = useContext(WalletActionContext)
  const customerModel = useAtomValue(customerModelAtom)
  const isEmbeddedCustomer = customerModel === CustomerModels.EMBEDDED

  const orchestratedCustomerWallet: OrchestratedCustomerWallet = useMemo(() => {
    const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
      getFiatWalletActionSupport({
        walletProperties: {
          currency: customerWallet?.balance?.currencyCode,
          lsid: customerWallet?.id,
        },
        bulkCapabilities,
      })

    return {
      ...customerWallet,
      supportsDeposits: supportsDeposits,
      supportsWithdrawals: supportsWithdrawals,
      supportsThirdParty: supportsPay,
      supportsInternalTransfers: supportsInternalTransfers,
    }
  }, [bulkCapabilities, customerWallet])

  const handleOnPayClick = (wallet: OrchestratedCustomerWallet) => {
    const walletAction: WalletAction = {
      key: 'pay',
      data: {
        id: wallet?.id,
      },
      metadata: {
        pay: {
          beneficiaryPaymentType: PayActionBeneficiaryTypes.UNDERLYING_CUSTOMER,
          customerReference: wallet?.customerReference,
          customerWalletId: wallet?.id,
        },
      },
    }

    setWalletModalAction(walletAction)
    setIsPayWalletModalOpen(true)
  }

  const handleOpenFiatDepositSheet = () => {
    if (orchestratedCustomerWallet) {
      const customerWalletDepositDetails: CustomerWalletDepositDetails = {
        wallet: orchestratedCustomerWallet,
        customer: customer,
      }
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWalletDepositDetails,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = () => {
    if (orchestratedCustomerWallet) {
      setWalletAction({
        actionId: ActionIds.MOVE,
        header: t('customers.moveFunds'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          sourceWalletType: WalletTransferType?.CUSTOMER,
          sourceCustomerWallet: orchestratedCustomerWallet,
          isEmbeddedCustomerModel: isEmbeddedCustomer,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenSendFiatSheet = () => {
    if (orchestratedCustomerWallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: orchestratedCustomerWallet?.id,
        meta: {
          customerWallet: orchestratedCustomerWallet,
        },
      })

      setIsSheetOpen(true)
    }
  }

  const handleOpenSendCryptoSheet = () => {
    if (orchestratedCustomerWallet) {
      setWalletAction({
        actionId: ActionIds?.SEND,
        header: t('wallets.send.contactStepHeader'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWallet: orchestratedCustomerWallet,
        },
      })
      setIsSheetOpen(true)
    }
  }

  const walletActionButtons = useMemo(() => {
    if (isFetchingCapabilities || isFetchingWalletLegacyId) return null

    const {
      isCryptoSendOptionEnabled,
      isLegacyPayoutOptionEnabled,
      isUnifiedPayoutOptionEnabled,
      isMoveOptionEnabled,
      isFiatDepositOptionEnabled,
    } = getCustomerWalletActions({
      enabledUnifiedFiatSendFlow,
      enableCustomerCryptoWalletTransactions,
      accountCapabilities,
      customerWallet: orchestratedCustomerWallet,
      isViewOnlyRole,
      customerModel,
    })

    return [
      ...(isLegacyPayoutOptionEnabled
        ? [
            <Button
              key="buttonPay"
              testId="buttonPay"
              onClick={() => handleOnPayClick(orchestratedCustomerWallet)}
              iconElement={<Icon name="CreditCard" />}
              disabled={isViewOnlyRole}
            >
              {t('pay')}
            </Button>,
          ]
        : []),
      ...(isUnifiedPayoutOptionEnabled
        ? [
            <Button
              key="sendFiat"
              testId="sendFiat"
              onClick={handleOpenSendFiatSheet}
              iconElement={<Icon name="CoinArrowOutCustom" />}
              disabled={isViewOnlyRole}
            >
              {t('wallets.fiatSend.sendFiat')}
            </Button>,
          ]
        : []),
      ...(isCryptoSendOptionEnabled
        ? [
            <Button
              key="sendCrypto"
              testId="sendCrypto"
              onClick={handleOpenSendCryptoSheet}
              disabled={isViewOnlyRole}
              iconElement={<Icon name="ArrowUpRight" />}
            >
              {t('payments.sendCrypto')}
            </Button>,
          ]
        : []),
      ...(isFiatDepositOptionEnabled
        ? [
            <Button
              key="depositButton"
              onClick={handleOpenFiatDepositSheet}
              iconElement={<Icon name="ArrowDownLeft" />}
              disabled={isViewOnlyRole}
            >
              {t('deposit')}
            </Button>,
          ]
        : []),
      ...(isMoveOptionEnabled
        ? [
            <Button
              key="moveButton"
              onClick={handleOpenMoveSheet}
              iconElement={<Icon name="ArrowsLeftRight" />}
              disabled={isViewOnlyRole}
            >
              {t('customers.move')}
            </Button>,
          ]
        : []),
    ]
  }, [
    bulkCapabilities,
    isFetchingCapabilities,
    isFetchingWalletLegacyId,
    isViewOnlyRole,
    enabledUnifiedFiatSendFlow,
    customerModel,
    orchestratedCustomerWallet,
  ])

  return <>{walletActionButtons}</>
}
